import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import LoadingState from '../components/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/Card';
import Button from '../components/Button';
import apiService from '../services/api';
import documentRequirementsService from '../services/documentRequirementsService';
import clubDocumentApi from '../services/clubDocumentApi';
import { FileText, AlertTriangle, Eye, Download, CheckCircle, FilePlus, Inbox, Clock, ShieldAlert } from 'lucide-react';
import { FilterBar, Listing } from '../components';
import styles from './UserDocuments.module.css';

const UserDocuments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubData, setClubData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadClubData();
  }, []);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const userInfo = await apiService.getCurrentUser();
      const club_id = userInfo.club_id;

      if (club_id) {
        // Use new context-based endpoint - club from Host/X-Club-ID header
        const clubResponse = await apiService.get('/api/clubs/public/club-info');
        const clubInfo = {
          id: club_id,
          name: clubResponse.name || clubResponse.display_name || 'Club Name',
          club_id: club_id
        };
        setClubData(clubInfo);

        // Load document requirement assignments
        await loadAssignments(clubInfo);
      } else {
        setError('Club information not found');
      }
    } catch (err) {
      console.error('Error loading club data:', err);
      setError('Failed to load club information');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async (clubInfo) => {
    try {
      const assignmentsData = await documentRequirementsService.getMyAssignments();

      // Fetch all templates once using tenant_id since that's what templates are stored with
      let clubTemplates = [];
      try {
        const templatesResponse = await clubDocumentApi.getTemplates(clubInfo.tenant_id);
        // API returns {templates: [...], total_count: ...}
        const responseData = templatesResponse.data || templatesResponse;
        clubTemplates = responseData.templates || [];
      } catch (err) {
        console.error('Error loading templates:', err);
      }

      // Enrich assignments with template details
      const enrichedAssignments = await Promise.all(
        assignmentsData.map(async (assignment) => {
          try {
            // Get the requirement details
            const requirement = await documentRequirementsService.getRequirement(assignment.requirement_id);

            // Find the template from the list
            const template = clubTemplates.find(t => t.id === requirement.template_id);

            return {
              ...assignment,
              requirement,
              template: template || null
            };
          } catch (err) {
            console.error(`Error loading details for assignment ${assignment.id}:`, err);
            return assignment;
          }
        })
      );

      setAssignments(enrichedAssignments);
    } catch (err) {
      console.error('Error loading assignments:', err);
      // Don't fail the whole page if assignments fail to load
      setAssignments([]);
    }
  };

  const handleCompleteDocument = (assignment) => {
    // Navigate to document completion wizard page
    navigate(`/documents/${assignment.id}/complete`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = (
      (assignment.requirement?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (assignment.template?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (assignment.requirement?.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesStatus = statusFilter === 'all' || assignment.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const pendingAssignments = filteredAssignments.filter(a => a.status?.toLowerCase() !== 'completed');
  const completedAssignments = filteredAssignments.filter(a => a.status?.toLowerCase() === 'completed');

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', className: 'status-pending', icon: <Clock size={14} /> },
      completed: { label: 'Completed', className: 'status-completed', icon: <CheckCircle size={14} /> },
      overdue: { label: 'Overdue', className: 'status-overdue', icon: <ShieldAlert size={14} /> },
      waived: { label: 'Waived', className: 'status-waived', icon: <FileText size={14} /> }
    };
    return statusMap[status?.toLowerCase()] || { label: status, className: 'status-default', icon: <FileText size={14} /> };
  };

  const handleViewDocument = async (assignment) => {
    try {
      // Check if this is a completed document with a PDF
      if (assignment.status?.toLowerCase() === 'completed' && assignment.pdf_url) {
        // Get the PDF and open in new window
        const filename = assignment.pdf_url.split('/').pop();
        const pdfBlob = await apiService.viewDocumentPDF(filename);

        // Create object URL and open in new window
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');

        // Clean up after a delay to allow the window to load
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else if (assignment.template?.content) {
        // For pending documents, show the template content in a new window
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${assignment.template?.title || 'Document'}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 40px;
                color: #333;
              }
              h1 {
                color: #27AE60;
                border-bottom: 2px solid #27AE60;
                padding-bottom: 10px;
              }
            </style>
          </head>
          <body>
            <h1>${assignment.template.title}</h1>
            ${assignment.template.content}
          </body>
          </html>
        `;
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      console.error('Error viewing document:', err);
      alert('Failed to view document');
    }
  };

  const handleDownloadDocument = async (assignment) => {
    try {
      console.log('Download - Assignment data:', assignment);
      console.log('Download - Status:', assignment.status);
      console.log('Download - PDF URL:', assignment.pdf_url);

      // Check if this is a completed document with a PDF
      const isCompleted = assignment.status?.toLowerCase() === 'completed';
      const hasPdfUrl = assignment.pdf_url && assignment.pdf_url.trim() !== '';

      console.log('Download - Is completed:', isCompleted);
      console.log('Download - Has PDF URL:', hasPdfUrl);

      if (isCompleted && hasPdfUrl) {
        // Download the PDF
        const filename = assignment.pdf_url.split('/').pop();
        console.log('Download - Filename:', filename);

        const pdfBlob = await apiService.downloadDocumentPDF(filename);
        console.log('Download - PDF Blob received:', pdfBlob.size, 'bytes');

        // Create download link
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${assignment.template?.title || 'document'}_completed.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.log('Download - Falling back to HTML content');
        console.log('Download - Reason: completed=' + isCompleted + ', hasPdf=' + hasPdfUrl);

        if (assignment.template?.content) {
          // Fallback: Download HTML content for pending documents
          const blob = new Blob([assignment.template.content], { type: 'text/html' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${assignment.template.title || 'document'}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          alert('No document content available');
        }
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document: ' + err.message);
    }
  };

  const renderAssignmentCard = (assignment) => {
    const status = getStatusBadge(assignment.status);
    const isCompleted = assignment.status?.toLowerCase() === 'completed';
    const is_overdue = assignment.status?.toLowerCase() === 'overdue';
    const due_date = assignment.due_date ? new Date(assignment.due_date) : null;

    return (
      <Card
        key={assignment.id}
        level={2}
        className={`${styles.assignmentCard} ${is_overdue ? styles.overdueCard : ''} ${!isCompleted ? styles.actionRequiredCard : ''}`}
      >
        {!isCompleted && (
          <div className={styles.cardHighlight} />
        )}
        <div className={styles.assignmentHeader}>
          <div className={styles.assignmentTitle}>
            <h3>{assignment.requirement?.title || assignment.template?.title || 'Document'}</h3>
            <span className={`${styles.statusBadge} ${styles[status.className]} ${!isCompleted ? styles.pulseBadge : ''}`}>
              {status.icon}
              {status.label}
            </span>
          </div>
          {due_date && !isCompleted && (
            <div className={`${styles.due_date} ${is_overdue ? styles.overdueText : ''}`}>
              {is_overdue ? 'OVERDUE' : 'Due'}: {due_date.toLocaleDateString()}
            </div>
          )}
        </div>

        {assignment.requirement?.description && (
          <p className={styles.assignmentDescription}>
            {assignment.requirement.description}
          </p>
        )}

        <div className={styles.assignmentMeta}>
          <span className={styles.metaItem}>
            <FileText size={14} /> {assignment.template?.category || 'Document'}
          </span>
          {assignment.requirement?.is_mandatory && (
            <span className={`${styles.metaItem} ${styles.mandatory}`}>
              <AlertTriangle size={14} /> Mandatory
            </span>
          )}
          {is_overdue && (
            <span className={`${styles.metaItem} ${styles.overdueMeta}`}>
              <Clock size={14} /> Action Required Immediately
            </span>
          )}
        </div>

        <div className={styles.assignmentActions}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewDocument(assignment)}
          >
            <Eye size={16} /> View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownloadDocument(assignment)}
          >
            <Download size={16} /> Download
          </Button>
          {!isCompleted ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleCompleteDocument(assignment)}
              className={styles.completeButton}
            >
              <CheckCircle size={16} /> Complete Now
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCompleteDocument(assignment)}
              style={{ borderColor: '#27AE60', color: '#27AE60' }}
            >
              <FilePlus size={16} /> Submit New Version
            </Button>
          )}
        </div>
      </Card>
    );
  };

  if (loading) {
    return <LoadingState message="Loading your documents..." />;
  }

  if (error) {
    return (
      <PageLayout maxWidth="1400px">
        <Section>
          <Card level={1}>
            <div style={{ textAlign: 'center', padding: 'var(--spacing-2xl)' }}>
              <AlertTriangle size={48} style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-md)' }} />
              <h2>Error Loading Documents</h2>
              <p>{error}</p>
              <Button onClick={loadClubData} variant="primary" style={{ marginTop: 'var(--spacing-md)' }}>Retry</Button>
            </div>
          </Card>
        </Section>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="1400px">
      <PageHeader
        title="My Documents"
        subtitle={`View and complete your assigned document requirements${clubData ? ` for ${clubData.name}` : ''}`}
      />

      <Section>
        <div className={styles.actionBar}>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className={styles.backButton}
          >
            ← Back to Dashboard
          </Button>
        </div>
      </Section>

      {/* Document Requirements List */}
      <Section>
        <FilterBar
          onClear={handleClearFilters}
          filters={[
            {
              type: 'search',
              name: 'search',
              placeholder: 'Search documents...',
              value: searchTerm,
              onChange: (e) => setSearchTerm(e.target.value)
            },
            {
              type: 'select',
              name: 'status',
              label: 'Filter by Status',
              value: statusFilter,
              onChange: (e) => setStatusFilter(e.target.value),
              options: [
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'completed', label: 'Completed' },
                { value: 'waived', label: 'Waived' }
              ]
            }
          ]}
        />
      </Section>

      <Section>
        <div className={styles.listingSection}>
          <Listing
            title="Action Required"
            items={pendingAssignments}
            renderItem={(assignment) => renderAssignmentCard(assignment)}
            emptyTitle="No Pending Actions"
            emptyMessage={searchTerm || statusFilter !== 'all' ? "No documents match your filters." : "You have no pending document requirements."}
            emptyIcon={<CheckCircle size={48} color="#27AE60" />}
          />
        </div>
      </Section>

      <Section>
        <div className={styles.listingSection}>
          <Listing
            title="Completed Documents"
            items={completedAssignments}
            renderItem={(assignment) => renderAssignmentCard(assignment)}
            emptyTitle="No Completed Documents"
            emptyMessage={searchTerm || statusFilter !== 'all' ? "No completed documents match your filters." : "No completed documents yet."}
            emptyIcon={<FileText size={48} style={{ opacity: 0.3 }} />}
          />
        </div>
      </Section>

      {/* Help Section */}
      <Section>
        <Card level={1} className={styles.helpCard}>
          <div className={styles.helpCardContent}>
            <h3 className={styles.helpCardHeading}>
              <FileText size={20} style={{ marginRight: '8px' }} /> About Document Requirements
            </h3>
            <div className={styles.helpCardBody}>
              <p className={styles.helpParagraph}>
                Document requirements are assigned to you based on your role, programs, and activities within the club.
                Each requirement includes a document template that you need to review and complete.
              </p>
              <ul className={styles.helpList}>
                <li className={styles.helpListItem}>
                  <strong>Mandatory documents</strong> may block certain actions until completed (e.g., registration, enrollment)
                </li>
                <li className={styles.helpListItem}>
                  <strong>Optional documents</strong> should be completed at your convenience
                </li>
                <li className={styles.helpListItem}>
                  Documents may appear in your <strong>inbox</strong> as pending tasks
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </Section>

      {/* Document Completion Wizard */}
      {/* Moved to separate route: /documents/:assignmentId/complete */}
    </PageLayout>
  );
};

export default UserDocuments;